/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useCallback, useEffect, useMemo } from "react";
import {
  Button,
  FormControl,
  FormControlLabel,
  FormLabel,
  Grid,
  Radio,
  RadioGroup,
  Checkbox,
  Tooltip,
} from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import { useFormik } from "formik";
import Loader from "../../components/loader/loader";
import { PopoutNotification } from "../../AppState/popoutNotification";
import NOTIFICATION_TYPE from "../../AppState/NotificationType";
import {
  EFormAction,
  EOnHandDeliveryStatus,
  ErrorMessages,
  XL_GRID_PROPS,
} from "../../constants";
import { isFormValueChanged } from "../../AppState/isFormValueChanged";
import { compareObjects } from "../../helpers";
import TooltipWithClickAway from "../../components/terminalPicker/TooltipWithClickAway";
import {
  intiOnHandInvestigationFormValue,
  onHandInvestigationSchema,
} from "../../schema/ohInvestigation";
import NoteBox from "../../components/noteBox/NoteBox";
import { IOhInvestigation } from "../../models/onHandDelivery";
import { OnHandInvestigationAPI } from "../../API/OnHandInvestigationAPI";
import { hasCompleteOnHandPermissionSelector } from "../../AppState/selectors/userPermissionsSelectors";
import { useCanViewByOwnershipPermission } from "../../@hooks/useCanViewByOwnershipPermission";

type Props = {
  osdNumber?: number;
  osdDetails?: any;
  setInvestigationStatus: (status: number) => void;
  osdType?: string;
  isEditMode?: boolean;
  isCleared?: boolean;
  onRefresh?: () => void;
  entryBy?: string;
  assignedTo?: string;
};

const OnHandDeliveryInvestigation: React.FC<Props> = ({
  osdNumber = 0,
  osdDetails,
  setInvestigationStatus,
  osdType,
  isEditMode = true,
  isCleared,
  onRefresh,
  entryBy,
  assignedTo,
}) => {
  const dispatch = useDispatch();
  const investigationDetails = useMemo(
    () => osdDetails?.investigation,
    [osdDetails?.investigation],
  );
  const hasCompleteOnHandPermission = useSelector(
    hasCompleteOnHandPermissionSelector,
  );

  const canViewCompleteButton = useCanViewByOwnershipPermission({
    enabledFor: hasCompleteOnHandPermission,
    entryBy,
    assignedTo,
  });

  const [loading, setLoading] = useState(false);
  const [resetTimestamps, setResetTimestamps] = useState<string>(
    Date.now().toString(),
  );
  const [status, setStatus] = useState(0);
  const [formFieldsDisabled, setFormFieldsDisabled] = useState(isEditMode);
  const [isCompleting, setIsCompleting] = useState<boolean>(false);
  const [initValues, setInitValues] = React.useState<any>(
    intiOnHandInvestigationFormValue,
  );

  const {
    errors,
    values,
    handleBlur,
    handleChange,
    resetForm,
    setFieldValue,
    setFieldError,
    validateForm,
    handleSubmit,
  } = useFormik({
    validationSchema: onHandInvestigationSchema,
    validateOnChange: false,
    validateOnBlur: true,
    initialValues: initValues,
    onSubmit: (_values) => {},
  });

  const enableEditing = () => {
    setFormFieldsDisabled(false);
  };

  // Handler for FormControlLabel (Radio buttons) - clears error immediately
  const handleRadioChangeWithClearError = useCallback(
    (e: React.SyntheticEvent<Element, Event>, checked: boolean) => {
      const target = e.target as HTMLInputElement;
      const { name } = target;
      if (errors[name]) {
        setFieldError(name, undefined);
      }
      handleChange(e as React.ChangeEvent<HTMLInputElement>);
    },
    [handleChange, errors, setFieldError],
  );

  // Clear error when setting field value (for checkboxes)
  const setFieldValueWithClearError = useCallback(
    (field: string, value: any, shouldValidate?: boolean) => {
      if (errors[field]) {
        setFieldError(field, undefined);
      }
      setFieldValue(field, value, shouldValidate);
    },
    [setFieldValue, errors, setFieldError],
  );

  const updateInitValue = (
    key: string,
    value: string | number | null | undefined,
  ) => {
    setInitValues((prevState: any) => {
      // Update the top-level property
      return {
        ...prevState,
        [key]: value,
      };
    });
  };

  const excludedKeys = new Set<keyof IOhInvestigation>([
    "hasEmailEntryError",
    "wereAllPartiesContactedWithOhn",
    "wasCommunicationSuccessful",
    "isF18NotesAccurate",
    "wasOhnSentByCreditDept",
  ]);
  // Follow-up action
  const normalizeYesNoFields = (params: IOhInvestigation) => {
    const yesNoFields: (keyof IOhInvestigation)[] = [
      "wereAllPartiesContactedWithOhn",
      "wasCommunicationSuccessful",
      "isF18NotesAccurate",
      "wasOhnSentByCreditDept",
    ];

    yesNoFields.forEach((field) => {
      const value = params[field];
      params[field] = value === "Y" || value === "N" ? value : undefined;
    });
  };

  const pruneConditionalFields = (params: IOhInvestigation) => {
    // No conditional fields left to prune
  };

  const handleApiSuccess = (
    statusNumber: number,
    params: IOhInvestigation,
    action: EFormAction,
    dispatch: any,
  ) => {
    setStatus(statusNumber);
    setIsCompleting(false);
    setInvestigationStatus(statusNumber);
    setFormFieldsDisabled(true);
    patchFormFieldValues(params);

    let message = ErrorMessages.investigationNoteSaved;
    if (action === EFormAction.complete)
      message = ErrorMessages.ohInvestigationNoteCompleted;
    if (action === EFormAction.credit) message = ErrorMessages.ohCreditHold;
    if (action === EFormAction.submit) message = ErrorMessages.investigationSubmitted;

    dispatch(
      PopoutNotification(
        NOTIFICATION_TYPE.SUCCESS,
        message,
        Date.now().toString(),
      ),
    );

    // Refresh data after successful action
    onRefresh?.();
  };

  const handleApiError = (err: unknown, dispatch: any) => {
    const errorMessage =
      typeof err === "string"
        ? err
        : "Exception Investigation failed. Please try again.";

    dispatch(
      PopoutNotification(
        NOTIFICATION_TYPE.ERROR,
        errorMessage,
        Date.now().toString(),
      ),
    );
    console.error("Exception Investigation error:", err);
  };

  const saveOrComplete = useCallback(
    async (action = EFormAction.save) => {
      const params: IOhInvestigation = { ...values };

      normalizeYesNoFields(params);
      pruneConditionalFields(params);

      const errors = await validateForm();
      if (
        (action !== EFormAction.save ||
          status === EOnHandDeliveryStatus.srpendingpproval) &&
        Object.keys(errors).length > 0
      ) {
        setIsCompleting(true);
        return;
      }

      setLoading(true);

      try {
        const { statusNumber } = await OnHandInvestigationAPI(
          params,
          osdNumber,
          action,
        );
        handleApiSuccess(statusNumber, params, action, dispatch);
      } catch (err) {
        handleApiError(err, dispatch);
      } finally {
        setLoading(false);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [values, dispatch, validateForm, osdNumber, osdType],
  );

  const resetToLastSaved = () => {
    resetForm({ values: initValues }); // This resets all values and validations correctly
    setResetTimestamps(Date.now().toString());
    document.getElementsByName('hasEmailEntryError')[0].focus();
  };

  const patchFormFieldValues = (formValues: IOhInvestigation) => {
    Object.entries(intiOnHandInvestigationFormValue).forEach(([key]) => {
      const typedKey = key as keyof IOhInvestigation;
      const value = formValues[typedKey] ?? undefined;

      setFieldValue(typedKey, value, Boolean(value));
      updateInitValue(typedKey, value);
    });
  };

  const renderFormErrorMessage = (
    filedName: string,
    className = "form-error",
  ) => {
    if (errors[filedName] && isCompleting)
      return <span className={className}>{errors[filedName] as string}</span>;
    return null;
  };

  const tolltip = (key: string, text: string) => {
    return <TooltipWithClickAway tooltipText={text} tooltipKey={key} />;
  };
  const renderRadioGroup = (
    fieldName: string,
    label: string = "",
    nTooltipKey: string = "",
    nTooltipText: string = "",
    yTooltipKey: string = "",
    yTooltipText: string = "",
  ) => {
    return (
      <Grid item {...XL_GRID_PROPS} className="pt-0">
        <FormControl className="radio-group question-group">
          <FormLabel>
            {label} <sup>*</sup>
          </FormLabel>
          <RadioGroup row name={fieldName}>
            <FormControlLabel
              value="Y"
              control={<Radio disabled={formFieldsDisabled} />}
              label="Yes"
              name={fieldName}
              checked={values[fieldName] === "Y"}
              onChange={handleRadioChangeWithClearError}
              onBlur={handleBlur}
            />
            {values[fieldName] === "Y" && yTooltipText && (
              <div className="tooltip-custom">
                {tolltip(yTooltipKey, yTooltipText)}
              </div>
            )}
            <FormControlLabel
              value="N"
              control={<Radio disabled={formFieldsDisabled} />}
              label="No"
              name={fieldName}
              checked={values[fieldName] === "N"}
              onChange={handleRadioChangeWithClearError}
              onBlur={handleBlur}
              className={
                yTooltipText && values[fieldName] === "Y"
                  ? "margin-left-20"
                  : ""
              }
            />
            {values[fieldName] === "N" && nTooltipText && (
              <div className="tooltip-custom">
                {tolltip(nTooltipKey, nTooltipText)}
              </div>
            )}
          </RadioGroup>
        </FormControl>
        {renderFormErrorMessage(fieldName)}
      </Grid>
    );
  };

  const renderCheckBox = (fieldName: string = "", label: string = "") => {
    return (
      <Grid item {...XL_GRID_PROPS} className="filter-margin">
        <FormControl className="radio-group question-group">
          <FormLabel>
            <FormControlLabel
              className="checkbox-shoartage"
              control={
                <Checkbox
                  disableRipple
                  checked={values[fieldName] === "Y"}
                  disabled={formFieldsDisabled}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                    setFieldValueWithClearError(
                      fieldName,
                      e.target.checked ? "Y" : "N",
                      true,
                    );
                  }}
                  name={fieldName}
                  sx={{ "& .MuiSvgIcon-root": { fontSize: 20 } }}
                />
              }
              label=""
            />
            {label} <sup>*</sup>
          </FormLabel>
        </FormControl>
        {renderFormErrorMessage(fieldName)}
      </Grid>
    );
  };

  const renderNotes = (message: string) => {
    return (
      <Grid item {...XL_GRID_PROPS} className="filter-margin">
        <NoteBox data-testid={message} message={`Note: ${message}`} />
      </Grid>
    );
  };

  const isEditable = ![
    EOnHandDeliveryStatus.needsCleared,
    EOnHandDeliveryStatus.followupInProgress,
    EOnHandDeliveryStatus.srpendingpproval,
  ].includes(status);

  const isCreditHoldStatus = status === EOnHandDeliveryStatus.creditHold;
  const isExceptionEntryStatus = status === EOnHandDeliveryStatus.exceptionEntry;

  // Render reusable button components
  const renderEditButton = () => (
    <Button
      variant="contained"
      onClick={enableEditing}
      className="assign ml mr-0"
      data-testid="edit-button"
    >
      Edit
    </Button>
  );

  const renderCancelButton = (label = "Cancel") => (
    <Button
      variant="contained"
      onClick={resetToLastSaved}
      className="clear-btn mr-0 ml"
      disabled={loading}
      data-testid={label === "Clear" ? "clear-button" : "cancel-button"}
    >
      {label}
    </Button>
  );

  const renderCompleteButton = () => (
    <Button
      variant="contained"
      onClick={() => saveOrComplete(EFormAction.complete)}
      className="entry ml"
      type="submit"
      data-testid="complete-button"
    >
      Complete
    </Button>
  );

  const renderUpdateCompleteButton = () => (
    <Button
      variant="contained"
      onClick={() => saveOrComplete(EFormAction.complete)}
      className="entry ml"
      type="submit"
      data-testid="update-complete-button"
    >
      Update & Complete
    </Button>
  );

  const renderUpdateSubmitButton = () => (
    <Button
      variant="contained"
      onClick={() => saveOrComplete(EFormAction.submit)}
      className="entry ml"
      type="submit"
      data-testid="update-submit-button"
    >
      Update & Submit
    </Button>
  );

  const renderSaveButton = () => (
    <Button
      variant="contained"
      onClick={() => saveOrComplete(EFormAction.save)}
      className="entry ml"
      type="submit"
      data-testid="save-button"
    >
      Save
    </Button>
  );

  const renderSubmitOrCreditButton = () => {
    if (values?.wasOhnSentByCreditDept === "Y") {
      return (
        <Tooltip title="Only report the failures to whoever entered the OH">
          <Button
            variant="contained"
            onClick={() => saveOrComplete(EFormAction.credit)}
            className="entry ml"
            type="submit"
            data-testid="credit-button"
          >
            Credit
          </Button>
        </Tooltip>
      );
    }
    return (
      <Button
        variant="contained"
        onClick={() => saveOrComplete(EFormAction.submit)}
        className="entry ml"
        type="submit"
        data-testid="submit-button"
      >
        Submit
      </Button>
    );
  };

  const actionButtons = () => {
    // Hide all action buttons if not editable or user lacks permission for creditHold status
    if (!isEditable || (!canViewCompleteButton && isCreditHoldStatus)) {
      return null;
    }

    // Credit Hold status with permission: Edit/Complete or Cancel/Update & Complete (or Update & Submit if wasOhnSentByCreditDept is N)
    if (canViewCompleteButton && isCreditHoldStatus) {
      return (
        <Grid item {...XL_GRID_PROPS} className="btn-group-submit">
          {formFieldsDisabled ? (
            <>
              {renderEditButton()}
              {renderCompleteButton()}
            </>
          ) : (
            <>
              {renderCancelButton()}
              {values?.wasOhnSentByCreditDept === "N"
                ? renderUpdateSubmitButton()
                : renderUpdateCompleteButton()}
            </>
          )}
        </Grid>
      );
    }

    // Default flow for other statuses
    return (
      <Grid item {...XL_GRID_PROPS} className="btn-group-submit">
        {formFieldsDisabled ? (
          renderEditButton()
        ) : (
          <>
            {renderCancelButton(isExceptionEntryStatus && !isEditMode ? "Clear" : "Cancel")}
            {renderSaveButton()}
            {renderSubmitOrCreditButton()}
          </>
        )}
      </Grid>
    );
  };

  const filteredKeys = useMemo(
    () =>
      Object.keys(intiOnHandInvestigationFormValue).filter(
        (key) => !excludedKeys.has(key as keyof IOhInvestigation),
      ),
    [],
  );

  useEffect(() => {
    if (!values?.wasOhnSentByCreditDept) return;

    const shouldClear = values.wasOhnSentByCreditDept === "Y";

    filteredKeys.forEach((key) => {
      const typedKey = key as keyof IOhInvestigation;
      const value = shouldClear ? "" : (initValues[typedKey] ?? undefined);
      setFieldValue(typedKey, value, true);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [values?.wasOhnSentByCreditDept]);

  useEffect(() => {
    dispatch(isFormValueChanged(!compareObjects(initValues, values)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [values, initValues]);

  useEffect(() => {
    if (!investigationDetails) return;

    patchFormFieldValues(investigationDetails);
    if (osdDetails?.investigationStatusNumber)
      setStatus(osdDetails?.investigationStatusNumber);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [investigationDetails]);

  return (
    <>
      <form action="" key={resetTimestamps} onSubmit={handleSubmit}>
        <Grid
          container
          spacing={2}
          className="filter-margin accordion-container"
        >
          <>
            {renderRadioGroup(
              "hasEmailEntryError",
              "Are there any entry errors on an email?",
            )}
            {values?.hasEmailEntryError === "Y" &&
              renderNotes(
                "Resend to failed parties with correct contact.",
              )}
            {renderRadioGroup(
              "wereAllPartiesContactedWithOhn",
              "Was the OHN sent to all parties of the BOL?",
            )}
            {values?.wereAllPartiesContactedWithOhn === "N" &&
              renderNotes("Send to all parties")}
            {renderRadioGroup(
              "wasCommunicationSuccessful",
              "Was all communication successful?",
            )}
            {values?.wasCommunicationSuccessful === "N" &&
              renderNotes(
                "Resend to failed parties with correct contact.",
              )}
            {renderCheckBox(
              "isF18NotesAccurate",
              "Confirm F18 notes are 100% accurate.",
            )}

            {renderRadioGroup(
              "wasOhnSentByCreditDept",
              "Was the OHN sent by the credit department?",
              "",
              "",
              "wasOhnSentByCreditDept1",
              "Only report the failures to whoever entered the OH.",
            )}
            {/* Action Buttons */}
            {!isCleared && actionButtons()}
          </>
        </Grid>
      </form>
      <Loader pageLoader={loading} />
    </>
  );
};

export default React.memo(OnHandDeliveryInvestigation);
